/**
 * useDisplayPanel Hook
 * Manages display panel data with Socket.IO real-time updates and adaptive polling fallback
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import displayService from '../services/displayService';
import { DisplayPanelData } from '../types/display';
import { getSocket } from '../services/socket';

export interface UseDisplayPanelOptions {
  clinicId: number;
  refreshInterval?: number; // milliseconds, default 5000
  onNewCall?: (queueNumber: number) => void;
  date?: string; // optional date override
}

export interface UseDisplayPanelReturn {
  data: DisplayPanelData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  socketConnected: boolean;
}

/**
 * Hook for managing display panel data with Socket.IO + adaptive polling
 */
export function useDisplayPanel(options: UseDisplayPanelOptions): UseDisplayPanelReturn {
  const { clinicId, refreshInterval = 5000, onNewCall, date } = options;

  const [data, setData] = useState<DisplayPanelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // Track last call number to detect changes
  const lastCallRef = useRef<number | null>(null);
  const onNewCallRef = useRef(onNewCall);

  // Keep onNewCall reference updated
  useEffect(() => {
    onNewCallRef.current = onNewCall;
  }, [onNewCall]);

  /**
   * Fetch display data from API
   */
  const fetchData = useCallback(async () => {
    try {
      const response = await displayService.getDisplayData(clinicId, date);
      setData(response);

      // Detect new call
      const currentNumber = response.currentCall?.queueNumber;
      if (currentNumber && currentNumber !== lastCallRef.current) {
        lastCallRef.current = currentNumber;
        onNewCallRef.current?.(currentNumber);
      }

      setError(null);
    } catch (err) {
      console.error('[DisplayPanel] Error fetching data:', err);
      setError('Erro ao carregar dados do painel');
    } finally {
      setLoading(false);
    }
  }, [clinicId, date]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchData();
  }, [fetchData]);

  // Socket.IO connection
  useEffect(() => {
    const socket = getSocket();

    const onConnect = () => {
      socket.emit('join:display', clinicId);
      setSocketConnected(true);
    };

    const onDisconnect = () => {
      setSocketConnected(false);
    };

    const onQueueUpdated = (payload: { clinicId: number }) => {
      if (payload.clinicId === clinicId) {
        fetchData();
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('queue:updated', onQueueUpdated);

    // If already connected when effect runs
    if (socket.connected) {
      socket.emit('join:display', clinicId);
      setSocketConnected(true);
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('queue:updated', onQueueUpdated);
      socket.emit('leave:display', clinicId);
    };
  }, [clinicId, fetchData]);

  // Initial fetch and adaptive polling
  useEffect(() => {
    fetchData();

    const interval = socketConnected ? 30000 : refreshInterval;
    if (interval > 0) {
      const timer = setInterval(fetchData, interval);
      return () => clearInterval(timer);
    }
  }, [fetchData, refreshInterval, socketConnected]);

  return { data, loading, error, refresh, socketConnected };
}

/**
 * Play notification sound using Web Audio API
 */
export function playNotificationSound(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) {
      console.warn('[DisplayPanel] Web Audio API not supported');
      return;
    }

    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configure sound: A5 note (880Hz), sine wave
    oscillator.frequency.value = 880;
    oscillator.type = 'sine';

    // Fade out effect
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);

    // Clean up after sound ends
    oscillator.onended = () => {
      audioContext.close();
    };
  } catch (err) {
    console.error('[DisplayPanel] Error playing notification sound:', err);
  }
}

export default useDisplayPanel;
